/**
 * PageViewBeacon — records every SPA route change so client-side navigation
 * lands in the same CloudWatch stream (`/lomi/prod/student-api`) as backend
 * requests.
 *
 * Why this exists: SPA route changes don't generate any HTTP request
 * to nginx for the new path. When Wondwosson Mitiku tapped Upgrade and
 * landed on the broken /subscription page on 2026-05-02, nginx never
 * saw the navigation; we couldn't prove from logs which page he was on
 * when he gave up. This beacon closes that gap.
 *
 * It rides the batched `track()` queue (POST /telemetry/events) rather than
 * firing its own POST per route change — at ~6k users the standalone beacon
 * was ~half a req/sec of pure overhead. The server still emits the dedicated
 * `pageview path=...` CloudWatch line for these events and skips the
 * engagement_events insert, so they remain log-only exactly as before. The
 * track queue flushes on visibilitychange/pagehide with keepalive, so a
 * navigate-away / tab-close still records the last page.
 */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { track } from '../services/api';

const API_BASE = process.env.REACT_APP_API_URL || '';

export const PageViewBeacon: React.FC = () => {
  const { pathname, search } = useLocation();
  const previous = useRef<string | null>(null);

  useEffect(() => {
    const path = pathname + (search || '');
    const fromPath = previous.current;
    previous.current = path;

    // Don't fire on first render in development if there's no API base
    // configured — keeps local dev quiet.
    if (!API_BASE && process.env.NODE_ENV === 'development') return;

    // Fire-and-forget; track() buffers and flushes in batches (and on unload).
    track('pageview', { path, from_path: fromPath });
  }, [pathname, search]);

  return null;
};
